import { GROUPS, resolveGroup } from "./groups.js";
import { REFERENCE_OIT } from "./reference.js";
import { scoringVersion } from "./version.js";
/** Poids par niveau de tâche (docs/SCORING.md §2). */
export const LEVEL_WEIGHTS = {
    high: 100,
    mid: 55,
    low: 15,
};
/** Un niveau absent ou invalide vaut `mid`. */
export const DEFAULT_LEVEL = "mid";
/** Barème de temps proposé à l'utilisateur en mode métier (docs/SCORING.md §3). */
export const HOUR_STEPS = [
    { libelle: "Jamais", heures: 0 },
    { libelle: "1-2 h", heures: 1.5 },
    { libelle: "3-6 h", heures: 4.5 },
    { libelle: "+6 h", heures: 8 },
];
/** Bornes du score. Jamais dépassées, quel que soit le profil. */
export const SCORE_MIN = 22;
export const SCORE_MAX = 97;
/** Pondérations de la formule : base du groupe et mix des tâches. */
export const BASE_WEIGHT = 0.55;
export const MIX_WEIGHT = 0.45;
/**
 * Les quatre niveaux d'exposition affichés. Les textes sont ceux de la maquette
 * (`GRADIENTS`) et sont reportés mot pour mot.
 */
export const GRADIENTS = [
    {
        max: 45,
        niveau: 1,
        libelle: "Exposition faible",
        texte: "Votre métier repose surtout sur des tâches que l'IA ne prend pas en charge. Votre enjeu est d'en tirer du temps, pas de vous protéger.",
    },
    {
        max: 62,
        niveau: 2,
        libelle: "Exposition modérée",
        texte: "Une partie de vos tâches va être automatisée, mais le cœur de votre métier résiste. Vous avez le temps de choisir votre repositionnement plutôt que de le subir.",
    },
    {
        max: 80,
        niveau: 3,
        libelle: "Exposition forte",
        texte: "La majorité de vos tâches quotidiennes sont automatisables aujourd'hui. Votre métier ne disparaîtra pas demain, mais son contenu va changer sans vous demander votre avis.",
    },
    {
        max: 101,
        niveau: 4,
        libelle: "Exposition critique",
        texte: 
        // Ce texte prédisait : « ce qui vous occupe aujourd'hui sera fait par un
        // outil avant la fin de l'année ». Une prophétie datée sur le travail
        // d'une personne, tirée d'un indice de tâches — et le contraire de ce
        // que le même produit écrit dans ses limites : « il ne prédit pas de
        // licenciement ». Ce qui est mesuré est plus fort que ce qui est
        // prophétisé, parce que le lecteur peut le vérifier.
        "Votre métier est dans la catégorie la plus exposée : l'essentiel de ce qui vous occupe est déjà réalisable par des outils accessibles aujourd'hui. Ce que vous en faites dans les mois qui viennent compte plus que le score lui-même.",
    },
];
export function normalizeLevel(value) {
    return value === "high" || value === "mid" || value === "low" ? value : DEFAULT_LEVEL;
}
/** Niveau d'exposition correspondant à un score. */
export function gradientForScore(score) {
    const found = GRADIENTS.find((g) => score < g.max) ?? GRADIENTS[GRADIENTS.length - 1];
    return { niveau: found.niveau, libelle: found.libelle, texte: found.texte };
}
/**
 * Effectif minimal, dans un groupe, avant de publier une comparaison
 * (docs/SCORING.md §5).
 */
export const COMPARAISON_SEUIL = 200;
/**
 * Comparaison aux autres profils du même groupe.
 *
 * Elle n'est calculée que sur des résultats réellement enregistrés. Tant que le
 * seuil n'est pas atteint, elle n'est pas disponible — et l'interface le dit,
 * au lieu d'afficher un rang dérivé du score, qui serait une affirmation sur
 * une population qui n'existe pas.
 *
 * @param score score du profil courant
 * @param distributionDuGroupe scores enregistrés pour le même groupe professionnel
 */
export function comparerAuGroupe(score, distributionDuGroupe = []) {
    const effectif = distributionDuGroupe.length;
    if (effectif < COMPARAISON_SEUIL) {
        return { disponible: false, seuil: COMPARAISON_SEUIL, effectif };
    }
    const endessous = distributionDuGroupe.filter((autre) => autre < score).length;
    return {
        disponible: true,
        seuil: COMPARAISON_SEUIL,
        effectif,
        percentile: Math.round((endessous / effectif) * 100),
    };
}
/**
 * Une profession de la source, passée par les bornes de notre indice — 29/09/2026.
 *
 * La dérivation ramène chaque profession sur l'échelle des bases, qui descend
 * à 0. L'indice d'une personne, lui, ne descend jamais sous 22. Comparer l'un à
 * l'autre plaçait mécaniquement **toute** personne des familles peu exposées
 * au-dessus de la médiane : le rapport du 28/09 écrivait « la médiane est à
 * 16, le vôtre est de 37 » à un métier de terrain, et 16 était hors d'atteinte
 * de n'importe quel indice STRATERA.
 *
 * Une profession entière, passée par notre formule avec sa propre exposition
 * pour base et pour répartition, donne `b × 0,55 + b × 0,45 = b` : elle reste
 * elle-même, puis subit les mêmes bornes que tout le monde. C'est la seule
 * transformation qui ne suppose rien, et elle met les deux côtés sur la même
 * échelle.
 */
export function surNotreEchelle(indiceProfession) {
    return Math.max(SCORE_MIN, Math.min(SCORE_MAX, indiceProfession));
}
/**
 * Situation face aux professions du groupe recensées par l'OIT.
 *
 * **Pourquoi ce repère existe.** La comparaison entre confrères demande 200
 * diagnostics dans un groupe, et ce seuil est juste : publier la distribution
 * de douze personnes comme « la mesure d'un métier » serait un faux signal.
 * Mais il a une conséquence que le produit a payée en silence — les tout
 * premiers visiteurs, ceux dont dépend l'amorçage, voyaient un score sans
 * aucun point de comparaison, c'est-à-dire une opinion plutôt qu'une mesure.
 * La fonctionnalité qui rend le résultat partageable exigeait que le partage
 * ait déjà eu lieu.
 *
 * **Pourquoi celui-ci est honnête.** Il ne comble pas le vide avec une
 * population imaginaire : il change de population, et le dit. Les 427
 * professions de la source sont ramenées sur notre échelle par la formule qui
 * donne déjà les bases — pas un paramètre libre de plus. Le lecteur peut
 * refaire le calcul : les données sont dans `data/oit/`, la méthode est
 * publiée, et `reference.test.ts` la rejoue.
 *
 * **Ce qu'on en dit, et pas plus.** Un dénombrement, jamais un pourcentage :
 * « votre indice dépasse celui de 9 des 13 professions » se vérifie ; « plus
 * exposé que 69 % » emprunterait à la statistique une précision que quatre à
 * treize professions ne portent pas. Les groupes les plus petits — RH en
 * compte quatre, JURIDIQUE cinq — le montrent d'eux-mêmes dans la phrase.
 *
 * Limite assumée : l'indice du visiteur est pondéré par les tâches et le temps
 * qu'il déclare, quand celui de la source est un indice de profession. Les
 * deux vivent sur la même échelle par construction, ce qui rend le rapport
 * lisible — pas identique. C'est écrit sur la page de méthode.
 */
export function comparerAuReferentiel(score, groupe) {
    // Déjà trié par la dérivation, et le bornage préserve l'ordre.
    const indices = REFERENCE_OIT[groupe].map(surNotreEchelle);
    const milieu = Math.floor(indices.length / 2);
    return {
        professions: indices.length,
        endessous: indices.filter((autre) => autre < score).length,
        min: indices[0],
        max: indices[indices.length - 1],
        mediane: indices.length % 2 === 0
            ? Math.round((indices[milieu - 1] + indices[milieu]) / 2)
            : indices[milieu],
    };
}
/** Poids temporel d'une tâche : le temps déclaré, ou 1 si non déclaré (mode CV). */
function taskHours(task) {
    const h = task.heures;
    return typeof h === "number" && Number.isFinite(h) && h > 0 ? h : 1;
}
/**
 * Calcule l'indice d'exposition.
 *
 *   mix   = Σ ( poids(niveau) × heures ) / Σ heures
 *   score = arrondi( base × 0,55 + mix × 0,45 ), borné entre 22 et 97
 *
 * Le calcul est déterministe : mêmes entrées, même score, indéfiniment. Ce
 * n'est pas la même chose que « deux profils identiques obtiennent le même
 * score » — le groupe et les niveaux de tâches viennent du référentiel métier,
 * et deux formulations différentes du même métier peuvent y pointer sur des
 * entrées différentes. C'est le référentiel, pas cette fonction, qui porte la
 * stabilité du score dans le temps (docs/SCORING.md §1).
 */
export function computeExposure(input) {
    const { code, fallback } = resolveGroup(input.groupe);
    const group = GROUPS[code];
    const taches = input.taches ?? [];
    if (taches.length === 0) {
        throw new Error("computeExposure : au moins une tâche est requise");
    }
    let weightedSum = 0;
    let hoursSum = 0;
    let high = 0;
    for (const task of taches) {
        const level = normalizeLevel(task.niveau);
        const hours = taskHours(task);
        weightedSum += LEVEL_WEIGHTS[level] * hours;
        hoursSum += hours;
        if (level === "high")
            high += 1;
    }
    const mix = weightedSum / hoursSum;
    const raw = Math.round(group.base * BASE_WEIGHT + mix * MIX_WEIGHT);
    const score = Math.max(SCORE_MIN, Math.min(SCORE_MAX, raw));
    return {
        score,
        gradient: gradientForScore(score),
        comparaison: comparerAuGroupe(score, input.distributionDuGroupe),
        repere: comparerAuReferentiel(score, code),
        base: group.base,
        mix,
        groupe: code,
        groupeLibelle: group.label,
        groupFallback: fallback,
        high,
        total: taches.length,
        scoringVersion,
    };
}
